import React from 'react';
import {
  Paper, BottomNavigation, BottomNavigationAction, alpha, useTheme
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ROUTES } from '../../constants/routes';
import { ROLES } from '../../constants';

// Icons
import DashboardIcon from '@mui/icons-material/Dashboard';
import EventIcon from '@mui/icons-material/Event';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArticleIcon from '@mui/icons-material/Article';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';

/**
 * MobileBottomNav - Fixed bottom navigation for mobile devices
 * Shows different items based on user role
 */
const MobileBottomNav = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();

  // Don't show if not logged in
  if (!currentUser) return null;

  const isOfficeOrAdmin = currentUser?.role === ROLES.ADMIN ||
                          currentUser?.role === ROLES.OFFICE ||
                          currentUser?.role === ROLES.DEV;

  // Define navigation items based on role
  const navItems = isOfficeOrAdmin ? [
    { label: 'Dashboard', value: ROUTES.DASHBOARD, icon: <DashboardIcon /> },
    { label: 'Kalender', value: ROUTES.CALENDAR, icon: <EventIcon /> },
    { label: 'Kunden', value: ROUTES.CLIENTS, icon: <ArticleIcon /> },
    { label: 'Profil', value: ROUTES.PROFILE, icon: <AccountCircleIcon /> },
  ] : [
    { label: 'Dashboard', value: ROUTES.DASHBOARD, icon: <DashboardIcon /> },
    { label: 'Kalender', value: ROUTES.CALENDAR, icon: <EventIcon /> },
    { label: 'Stunden', value: ROUTES.WORK_HOURS, icon: <AccessTimeIcon /> },
    { label: 'Profil', value: ROUTES.PROFILE, icon: <AccountCircleIcon /> },
  ];

  // Determine current value based on path
  const getCurrentValue = () => {
    const path = location.pathname;
    // Check for exact match first
    const exactMatch = navItems.find(item => item.value === path);
    if (exactMatch) return exactMatch.value;
    // Check for prefix match (e.g., /clients/123 matches /clients)
    const prefixMatch = navItems.find(item =>
      item.value !== ROUTES.DASHBOARD && path.startsWith(item.value)
    );
    if (prefixMatch) return prefixMatch.value;
    // Default to dashboard if on any other authenticated page
    return ROUTES.DASHBOARD;
  };

  const handleChange = (event, newValue) => {
    navigate(newValue);
  };

  return (
    <Paper
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: theme.zIndex.appBar + 1,
        borderTop: `1px solid ${theme.palette.divider}`,
        // Safe area for iOS devices
        pb: 'env(safe-area-inset-bottom)',
      }}
      elevation={8}
    >
      <BottomNavigation
        value={getCurrentValue()}
        onChange={handleChange}
        showLabels
        sx={{
          height: 64,
          bgcolor: theme.palette.background.paper,
          '& .MuiBottomNavigationAction-root': {
            minWidth: 'auto',
            py: 1,
            color: theme.palette.text.secondary,
            '&.Mui-selected': {
              color: theme.palette.primary.main,
            },
          },
          '& .MuiBottomNavigationAction-label': {
            fontSize: '0.7rem',
            '&.Mui-selected': {
              fontSize: '0.75rem',
              fontWeight: 600,
            },
          },
        }}
      >
        {navItems.map((item) => (
          <BottomNavigationAction
            key={item.value}
            label={item.label}
            value={item.value}
            icon={item.icon}
          />
        ))}
      </BottomNavigation>
    </Paper>
  );
};

export default MobileBottomNav;
